import { useState, useEffect, useRef } from 'react';
import { Loader2, AlertCircle } from 'lucide-react';

interface AuthenticatedImageProps {
  src: string;
  alt: string;
  className?: string;
  onError?: () => void;
}

const AUTHENTICATED_IMAGE_FETCH_TIMEOUT_MS = 30_000;
const DIRECT_IMAGE_LOAD_TIMEOUT_MS = 30_000;

export function AuthenticatedImage({ src, alt, className, onError }: AuthenticatedImageProps) {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [missing, setMissing] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const createdBlobUrlRef = useRef<string | null>(null);
  const mountedRef = useRef(true);
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      // Clean up on unmount - abort with reason
      if (abortControllerRef.current) {
        try {
          abortControllerRef.current.abort('Component unmounting');
        } catch (e) {
          // Ignore abort errors on cleanup
        }
      }
      // Clean up blob URL we created on unmount
      if (createdBlobUrlRef.current) {
        URL.revokeObjectURL(createdBlobUrlRef.current);
        createdBlobUrlRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    let directImageTimeoutId: ReturnType<typeof setTimeout> | undefined;
    let fetchTimeoutId: ReturnType<typeof setTimeout> | undefined;
    let localCreatedBlobUrl: string | null = null;

    const loadImage = async () => {
      // Cancel any pending requests with reason
      if (abortControllerRef.current) {
        try {
          abortControllerRef.current.abort('New image load initiated');
        } catch (e) {
          // Ignore any errors during abort
        }
      }
      abortControllerRef.current = new AbortController();

      // Clean up previous blob URL we created
      if (createdBlobUrlRef.current) {
        URL.revokeObjectURL(createdBlobUrlRef.current);
        createdBlobUrlRef.current = null;
        setImageUrl(null);
      }

      if (!src) {
        setLoading(false);
        setImageUrl(null);
        return;
      }

      // If it's already a data URL, use it directly
      if (src.startsWith('data:')) {
        setImageUrl(src);
        setLoading(false);
        setError(false);
        return;
      }

      // If it's a blob URL, use it directly (external blob URL from file input)
      if (src.startsWith('blob:')) {
        setImageUrl(src);
        setLoading(false);
        setError(false);
        return;
      }

      // If it's a full URL (http/https), try direct loading first
      if (src.startsWith('http://') || src.startsWith('https://')) {
        // Test if the image loads directly
        const img = new Image();
        img.onload = () => {
          if (directImageTimeoutId) {
            clearTimeout(directImageTimeoutId);
            directImageTimeoutId = undefined;
          }
          if (mountedRef.current) {
            setImageUrl(src);
            setLoading(false);
            setError(false);
          }
        };
        img.onerror = () => {
          if (directImageTimeoutId) {
            clearTimeout(directImageTimeoutId);
            directImageTimeoutId = undefined;
          }
          // If direct loading fails, try with authentication
          fetchAuthenticatedImage();
        };
        directImageTimeoutId = setTimeout(() => {
          img.onload = null;
          img.onerror = null;
          fetchAuthenticatedImage();
        }, DIRECT_IMAGE_LOAD_TIMEOUT_MS);
        img.src = src;
        return;
      }

      // For relative URLs, always fetch with authentication
      fetchAuthenticatedImage();
    };

    const fetchAuthenticatedImage = async () => {
      try {
        setLoading(true);
        setError(false);
        setMissing(false);

        // Build the full URL if it's a relative path
        let fullUrl = src;
        if (!src.startsWith('http://') && !src.startsWith('https://')) {
          // Get the base URL from the current location
          const baseUrl = window.location.origin;
          fullUrl = src.startsWith('/') ? `${baseUrl}${src}` : `${baseUrl}/${src}`;
        }

        if (retryCount > 0) {
          fullUrl = `${fullUrl}${fullUrl.includes('?') ? '&' : '?'}retry=${retryCount}`;
        }

        // Always log fetching to debug the issue
        console.log(`[AuthenticatedImage] Fetching: ${fullUrl}`);

        const controller = abortControllerRef.current;
        fetchTimeoutId = setTimeout(() => {
          controller?.abort('Authenticated image fetch timed out');
        }, AUTHENTICATED_IMAGE_FETCH_TIMEOUT_MS);

        const response = await fetch(fullUrl, {
          method: 'GET',
          credentials: 'include',
          headers: {
            Accept: 'image/*,*/*',
          },
          signal: controller?.signal,
        });

        if (!response.ok) {
          // A missing object cannot recover through automatic retries.
          if (response.status === 404) {
            if (mountedRef.current) {
              setMissing(true);
              setError(true);
              setLoading(false);
              onError?.();
            }
            return;
          }
          console.error(
            `[AuthenticatedImage] Failed to fetch: ${response.status} ${response.statusText}`
          );

          // For auth errors, retry once
          if (response.status === 401 && retryCount === 0) {
            timeoutId = setTimeout(() => {
              setRetryCount(1);
            }, 500);
            return;
          }

          throw new Error(`Failed to load image: ${response.status}`);
        }

        // Get the content type
        const contentType = response.headers.get('Content-Type') || 'image/jpeg';

        // Accept various image types including octet-stream (for HEIC)
        const validTypes = ['image/', 'application/octet-stream'];
        const isValidType = validTypes.some((type) => contentType.includes(type));

        if (!isValidType) {
          console.warn(
            `[AuthenticatedImage] Unexpected content type: ${contentType}, attempting to display anyway`
          );
        }

        // Convert response to blob
        const blob = await response.blob();
        console.log(
          `[AuthenticatedImage] Response received - Status: ${response.status}, Blob size: ${blob.size} bytes, type: ${blob.type}`
        );

        // Verify blob is not empty - 1 byte responses are invalid
        if (blob.size <= 1) {
          console.error(
            `[AuthenticatedImage] Invalid image response: only ${blob.size} bytes received for ${fullUrl}`
          );
          throw new Error(`Invalid image response: only ${blob.size} bytes received`);
        }

        // If blob type is missing or generic, try to set it based on content-type
        let finalBlob = blob;
        if (!blob.type || blob.type === 'application/octet-stream') {
          finalBlob = new Blob([blob], { type: contentType || 'image/jpeg' });
        }

        // Create object URL from blob
        const newObjectUrl = URL.createObjectURL(finalBlob);
        createdBlobUrlRef.current = newObjectUrl;
        localCreatedBlobUrl = newObjectUrl;

        // Log all successful loads
        console.log(
          `[AuthenticatedImage] Successfully loaded image: ${blob.size} bytes, Object URL: ${newObjectUrl}`
        );

        if (mountedRef.current) {
          setImageUrl(newObjectUrl);
          setLoading(false);
          setError(false);
        }
      } catch (err: any) {
        // Check if the request was aborted - this is expected during cleanup
        // Handle various abort error formats
        const isAbortError =
          err?.name === 'AbortError' ||
          err?.message?.includes('aborted') ||
          err?.code === 'ABORT_ERR' ||
          err?.code === 20 ||
          abortControllerRef.current?.signal.aborted ||
          (err instanceof DOMException && err.code === DOMException.ABORT_ERR);

        if (isAbortError) {
          // Silent return - this is normal cleanup behavior when navigating away
          // No console log needed for expected behavior
          return;
        }

        // Also check if component unmounted during the fetch
        if (!mountedRef.current) {
          return;
        }

        // Only log actual errors, not abort-related ones
        console.error('[AuthenticatedImage] Error loading image:', err);

        // Retry logic for transient errors (only if component is still mounted)
        if (retryCount < 2 && mountedRef.current && !abortControllerRef.current?.signal.aborted) {
          timeoutId = setTimeout(
            () => {
              if (mountedRef.current) {
                setRetryCount((prev) => prev + 1);
              }
            },
            1000 * (retryCount + 1)
          ); // Exponential backoff
        } else if (mountedRef.current && !abortControllerRef.current?.signal.aborted) {
          // Only set error state if component is still mounted
          setError(true);
          setLoading(false);
          onError?.();
        }
      } finally {
        if (fetchTimeoutId) {
          clearTimeout(fetchTimeoutId);
          fetchTimeoutId = undefined;
        }
      }
    };

    loadImage();

    // Cleanup function
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      if (directImageTimeoutId) {
        clearTimeout(directImageTimeoutId);
      }
      if (fetchTimeoutId) {
        clearTimeout(fetchTimeoutId);
      }
      // Clean up blob URL created in this effect
      if (localCreatedBlobUrl && localCreatedBlobUrl !== createdBlobUrlRef.current) {
        URL.revokeObjectURL(localCreatedBlobUrl);
      }
    };
  }, [src, retryCount]);

  if (loading) {
    return (
      <div className={`flex items-center justify-center bg-gray-100 ${className}`}>
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (error || !imageUrl) {
    return (
      <div className={`flex items-center justify-center bg-gray-100 ${className}`}>
        <div className="text-center p-4">
          <AlertCircle className="h-8 w-8 text-gray-400 mx-auto mb-2" />
          <p className="text-sm text-gray-500 mb-1">{missing ? 'Photo file not found' : 'Unable to load image'}</p>
          <p className="text-xs text-gray-400">{missing
            ? 'The saved photo file is missing. Recover or re-upload the original photo.'
            : 'The image may be processing or unavailable'}</p>
          <button
            onClick={() => {
              setError(false);
              setLoading(true);
              setRetryCount((count) => count + 1);
              // This will trigger the useEffect to reload
            }}
            className="text-xs text-blue-600 hover:underline mt-2"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <img
      src={imageUrl}
      alt={alt}
      className={className}
      onLoad={() => {
        // Image loaded successfully
        console.log('[AuthenticatedImage] Image element loaded successfully');
        if (mountedRef.current) {
          setError(false);
          setLoading(false);
        }
      }}
      onError={(e) => {
        if (!mountedRef.current) return;

        const target = e.target as HTMLImageElement;
        console.error('[AuthenticatedImage] Image element failed to load:', target.src);

        // Don't retry blob URLs, just show error
        if (target.src.startsWith('blob:')) {
          setError(true);
          setLoading(false);
          onError?.();
        } else if (retryCount < 2) {
          // For other URLs, retry with authentication
          setRetryCount((prev) => prev + 1);
        } else {
          setError(true);
          setLoading(false);
          onError?.();
        }
      }}
    />
  );
}
